import React, { useCallback, useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Link } from 'react-router-dom';

// Components
import DataTable from '../../components/DataTable';
import InterviewDetails from '../../components/popup/InterviewDetails';
import AddEditFeedback from '../../components/popup/AddEditFeedback';
import { ActionButtonsForSceduledInterview, ResumeButton } from '../../components/ActionButtons';

// Redux-Actions
import { interviewerInterviewDetailAction } from '../../redux/actions/userDetails';
import {
  cancelInterviewRoundAction,
  getInterviewSheduleList
} from '../../redux/actions/interviews';

// Redux-Slices
import { getNotification } from '../../redux/slices/notification';

// Constants
import { SCHEDULED_INTERVIEW_COLUMNS } from '../../constants/tableColumns';
import {
  ACTION_TYPE,
  DEBOUNCE_TIMEOUT,
  INITIAL_PAGE_NUMBER,
  INITIAL_SIZE,
  INTERVIEW_TYPE,
  InterviewType,
  TABLE_NAME
} from '../../constants/common';

// Utils
import { formatTime, getFormattedDateView } from '../../utils/date';
import { debounce } from '../../utils/debounce';
import { wordCapitalize, downloadPdf } from '../../utils/common';
import moment from 'moment';
import AddHrFeedback from '../../components/popup/AddHrFeedback';
import ConfirmDialogWithReason from '../../components/ConfirmDialogWithReasonForInterview';

const pathBase = `${process.env.REACT_APP_API_ENDPOINT}/public/uploads/resume`;

const InterviewSchedule = () => {
  // Redux hooks.
  const dispatch = useDispatch();
  const { interviewSchedule, userDetails } = useSelector((state) => state);
  const { isNotification } = useSelector((state) => state.notification);

  // React useState hooks.
  const [scheduleInterviewList, setScheduleInterviewList] = useState([]);
  const [scheduleInterviewExport, setScheduleInterviewExport] = useState([]);
  const [interviewRoundMode, setInterviewRoundMode] = useState(null);
  const [interviewId, setInterviewId] = useState(null);
  const [interviewDetails, setInterviewDetails] = useState([]);
  const [showPopup, setShowPopup] = useState(false);
  const [typeInterview, setTypeInterview] = useState(null);
  const [showFeedbackPopup, setShowFeedbackPopup] = useState(false);
  const [showHRFeedbackPopup, setShowHRFeedbackPopup] = useState(false);
  const [getCandidateName, setCandidateName] = useState('');
  const [getCandidateId, setCandidateId] = useState(null);
  const [interviewRoundId, setInterviewRoundId] = useState(null);
  const [cancelConfirmBoxVisibility, setCancelConfirmBoxVisibility] = useState(false);
  const [interviewCategory, setInterviewCategory] = useState(null);
  const [tableStates, setTableStates] = useState({
    searchValue: '',
    pageNumber: INITIAL_PAGE_NUMBER,
    size: INITIAL_SIZE,
    sortBy: 'DateModified',
    orderBy: 'desc'
  });

  const openResumeLink = (link) => window.open(link, '_blank');

  // React useEffect hooks.
  const onTogglePopup = () => setShowPopup((prevState) => !prevState);
  const onToggleFeedbackPopup = (data) => setShowFeedbackPopup(data);
  const onToggleHRFeedbackPopup = (data) => setShowHRFeedbackPopup(data);

  useEffect(() => {
    dispatch(getInterviewSheduleList(tableStates));
    if (isNotification) {
      dispatch(getNotification(false));
    }
  }, [dispatch, tableStates, isNotification]);

  useEffect(() => {
    const modifiedList = interviewSchedule?.data?.map((interview) => {
      const { InterviewRound } = interview;
      const { Interview, ID, Description, StartDateOfInterviewRound } = InterviewRound;
      const candidateName = `${
        Interview?.Candidate?.FirstName ? wordCapitalize(Interview?.Candidate?.FirstName) : ''
      } ${
        wordCapitalize(Interview?.Candidate?.LastName)
          ? wordCapitalize(Interview?.Candidate?.LastName)
          : ''
      }`;
      return {
        ...interview,
        CandidateName: (
          <Link to="#" className="table-view-popup-link" onClick={() => handleView(ID)}>
            {candidateName}
          </Link>
        ),
        HrName: `${
          InterviewRound.HROfRound?.FirstName
            ? wordCapitalize(InterviewRound.HROfRound?.FirstName)
            : ''
        } ${
          InterviewRound.HROfRound?.LastName
            ? wordCapitalize(InterviewRound.HROfRound?.LastName)
            : ''
        }`,
        'Start Date': `${getFormattedDateView(
          InterviewRound.StartDateOfInterviewRound
        )} ${formatTime(InterviewRound.StartDateOfInterviewRound)}`,
        Technology: (
          <ul className="technology-list">
            {Interview?.Candidate?.UMap && Interview?.Candidate?.UMap?.length > 0
              ? Interview.Candidate.UMap?.map((tech) => (
                  <li
                    key={tech.MapT.Technology}
                    style={{ backgroundColor: `${tech?.MapT.TechnologyColor}` }}
                    className="technology-badge">
                    {tech.MapT.Technology}
                  </li>
                ))
              : []}
          </ul>
        ),
        JobDesignation:
          Interview?.JobDesignation && wordCapitalize(Interview?.JobDesignation?.Designation),

        RoundOfInterview: `${INTERVIEW_TYPE.filter(
          (num) => num.code === InterviewRound?.InterviewRound
        ).map((type) => type.name)}`,
        Description: Description || '',
        resume: (
          <div className="flex">
            {Interview?.Candidate?.ResumeOfCandidate && (
              <>
                <ResumeButton
                  onClick={() =>
                    openResumeLink(
                      `${pathBase}/${Interview?.Candidate?.ID}/${Interview?.Candidate?.ResumeOfCandidate?.FilePath}`
                    )
                  }
                />
                <i
                  data-tip="Resume Download"
                  onClick={() =>
                    downloadPdf(
                      pathBase,
                      Interview?.Candidate?.ID,
                      Interview?.Candidate?.ResumeOfCandidate?.FilePath
                    )
                  }
                  className="pi pi-fw pi-download"
                />
              </>
            )}
          </div>
        ),
        action: (
          <div className="flex align-items-center">
            <ActionButtonsForSceduledInterview
              onClick={(actionType) =>
                handleRowActions(
                  actionType,
                  InterviewRound.ID,
                  InterviewRound?.InterviewRoundMode,
                  InterviewRound?.InterviewRound,
                  candidateName,
                  InterviewRound?.Interview?.Candidate?.ID,
                  Interview?.InterviewCategory
                )
              }
              disabled={moment(moment()).isBefore(StartDateOfInterviewRound, 'minute')}
            />
          </div>
        )
      };
    });
    setScheduleInterviewList(modifiedList);
  }, [interviewSchedule]);

  useEffect(() => {
    const modifiedList = interviewSchedule?.data?.map((interview) => {
      const { InterviewRound } = interview;
      const { Interview } = InterviewRound;
      const name = `${
        Interview?.Candidate?.FirstName ? wordCapitalize(Interview.Candidate?.FirstName) : ''
      } ${Interview?.Candidate?.LastName ? wordCapitalize(Interview.Candidate?.LastName) : ''}`;
      const Type = Interview?.Candidate?.UMap?.length
        ? Interview.Candidate.UMap.map((item) => item.MapT.Technology).join(', ')
        : [];
      return {
        CandidateName: name,
        HrName: `${
          InterviewRound.HROfRound?.FirstName
            ? wordCapitalize(InterviewRound.HROfRound?.FirstName)
            : ''
        } ${
          InterviewRound.HROfRound?.LastName
            ? wordCapitalize(InterviewRound.HROfRound?.LastName)
            : ''
        }`,
        'Start Date': `${getFormattedDateView(
          InterviewRound.StartDateOfInterviewRound
        )} ${formatTime(InterviewRound.StartDateOfInterviewRound)}`,

        Technology: Type,

        JobDesignation:
          Interview?.JobDesignation && wordCapitalize(Interview?.JobDesignation?.Designation),

        RoundOfInterview: `${INTERVIEW_TYPE.filter(
          (num) => num.code === InterviewRound?.InterviewRound
        ).map((type) => type.name)}`
      };
    });
    setScheduleInterviewExport(modifiedList);
  }, [interviewSchedule]);

  const modifyInterviewDetails = useCallback(() => {
    if (userDetails?.Interview?.Candidate && userDetails?.HROfRound) {
      setInterviewDetails({
        'Interview Details': {
          'Start Date': `${getFormattedDateView(
            userDetails?.StartDateOfInterviewRound
          )} ${formatTime(userDetails?.StartDateOfInterviewRound || '')} `,
          'End Date': `${getFormattedDateView(userDetails?.EndDateOfInterviewRound)} ${formatTime(
            userDetails?.EndDateOfInterviewRound || ''
          )} `,
          'Interview Type': `${INTERVIEW_TYPE.filter(
            (num) => num.code === userDetails?.InterviewRound
          ).map((type) => type.name)}`,
          Technology:
            userDetails?.Interview?.Candidate.UMap &&
            userDetails?.Interview?.Candidate.UMap.length > 0
              ? userDetails?.Interview?.Candidate.UMap?.map((tech) =>
                  wordCapitalize(tech.MapT.Technology)
                ).join(', ')
              : null,
          'Job Designation': userDetails?.Interview?.JobDesignation
            ? userDetails?.Interview?.JobDesignation?.Designation
            : '--'
        },
        'Candidate Details': {
          Name: `${wordCapitalize(userDetails?.Interview?.Candidate?.FirstName) || ''} ${
            wordCapitalize(userDetails?.Interview?.Candidate?.LastName) || ''
          }`,
          // Email: (
          //   <div className="center">
          //     <span>{userDetails?.Interview?.Candidate?.Email || '--'}</span>
          //     {userDetails?.Interview?.Candidate?.Email && (
          //       <>
          //         <i
          //           data-tip="Copied"
          //           data-event="click focus"
          //           data-for={`copyIcon_${userDetails?.Interview?.Candidate?.Email}`}
          //           id={`copyIcon_${userDetails?.Interview?.Candidate?.Email}`}
          //           className="pi pi-fw pi-copy ml-1 copy-icon"
          //         />
          //         <ReactTooltip
          //           id={`copyIcon_${userDetails?.Interview?.Candidate?.Email}`}
          //           globalEventOff="click"
          //           afterShow={() => {
          //             navigator.clipboard.writeText(userDetails?.Interview?.Candidate?.Email);
          //             setTimeout(() => {
          //               ReactTooltip.hide();
          //             }, 500);
          //           }}
          //         />
          //       </>
          //     )}
          //   </div>
          // ),
          Experience:
            `${userDetails?.Interview?.Candidate?.YearsOfExperience} Years` +
            `${
              userDetails?.Interview?.Candidate?.MonthsOfExperience
                ? ` ${userDetails?.Interview?.Candidate?.MonthsOfExperience} Months`
                : ''
            }`
        },
        'HR Details': {
          Name: `${userDetails?.HROfRound?.FirstName || ''} ${
            userDetails?.HROfRound?.LastName || ''
          }`
          // Email: (
          //   <div className="center">
          //     <span>{userDetails?.HR?.Email || '--'}</span>
          //     {userDetails?.HR?.Email && (
          //       <>
          //         <i
          //           data-tip="Copied"
          //           data-event="click focus"
          //           data-for={`copyIcon_${userDetails?.HR?.Email}`}
          //           id={`copyIcon_${userDetails?.HR?.Email}`}
          //           className="pi pi-fw pi-copy ml-1 copy-icon"
          //         />
          //         <ReactTooltip
          //           id={`copyIcon_${userDetails?.HR?.Email}`}
          //           globalEventOff="click"
          //           afterShow={() => {
          //             navigator.clipboard.writeText(userDetails?.HR?.Email);
          //             setTimeout(() => {
          //               ReactTooltip.hide();
          //             }, 500);
          //           }}
          //         />
          //       </>
          //     )}
          //   </div>
          // )
        }
      });
    }
  }, [userDetails]);

  useEffect(() => {
    modifyInterviewDetails();
  }, [userDetails, modifyInterviewDetails]);

  useEffect(() => {
    if (!interviewRoundId) {
      setCancelConfirmBoxVisibility(false);
    }
  }, [interviewRoundId]);

  useEffect(() => {
    interviewId && dispatch(interviewerInterviewDetailAction(interviewId));
  }, [dispatch, interviewId]);

  const handlePagination = (pageNumber, size) =>
    setTableStates((prevState) => {
      return {
        ...prevState,
        pageNumber,
        size
      };
    });

  const handleSearch = (searchValue) => {
    setTableStates((prevState) => {
      return {
        ...prevState,
        searchValue
      };
    });
  };

  const handleSort = (sortBy) => {
    setTableStates((prevState) => {
      return {
        ...prevState,
        sortBy,
        orderBy: prevState.orderBy === 'asc' ? 'desc' : 'asc'
      };
    });
  };
  const handleView = (ID) => {
    if (ID) {
      onTogglePopup();
      setInterviewId(ID);
    }
  };

  const handleRowActions = (
    actionType,
    rowId,
    mode,
    type,
    candidateName,
    candidateId,
    interviewCategory
  ) => {
    if (actionType === ACTION_TYPE.FEEDBACK) {
      setCandidateName(candidateName);
      setTypeInterview(type);
      setInterviewRoundMode(mode);
      setCandidateId(candidateId);
      setInterviewCategory(interviewCategory);
      if (rowId) {
        setInterviewId(rowId);
        if (type === InterviewType['HR Round']) {
          onToggleHRFeedbackPopup(true);
        } else {
          onToggleFeedbackPopup(true);
        }
      }
    } else if (actionType === ACTION_TYPE.CANCEL) {
      setCandidateName(candidateName);
      setInterviewRoundId(rowId);
      setCancelConfirmBoxVisibility(true);
    }
  };

  return (
    <>
      <AddHrFeedback
        id={interviewId}
        onHide={onToggleHRFeedbackPopup}
        show={showHRFeedbackPopup}
        interviewType={typeInterview}
        ByInterviewer
        interviewCategory={interviewCategory}
        confirmDialogMessage={
          <>
            Are you sure you want to submit the feedback for the{' '}
            <strong>{getCandidateName ? wordCapitalize(getCandidateName) : ''}</strong> candidate?
          </>
        }
      />

      <AddEditFeedback
        id={interviewId}
        onHide={onToggleFeedbackPopup}
        show={showFeedbackPopup}
        interviewType={typeInterview}
        interviewRoundMode={interviewRoundMode}
        candidateId={getCandidateId || ''}
        interviewRoundId={interviewId}
        interviewCategory={interviewCategory}
        confirmDialogMessage={
          <>
            Are you sure you want to submit the feedback for the{' '}
            <strong>{getCandidateName ? wordCapitalize(getCandidateName) : ''}</strong>
            {''} candidate?
          </>
        }
      />

      <ConfirmDialogWithReason
        bodyTitle={
          <>
            Are you sure you want to cancel the interview for{' '}
            <strong> &nbsp;{getCandidateName}?</strong>
          </>
        }
        interviewId={interviewRoundId}
        dialogHeader="Cancel interview"
        setInterviewId={setInterviewRoundId}
        displayConfirmAction={cancelConfirmBoxVisibility}
        ishandleSuccess
        isReasonRequired
        onSuccess={(payload) => {
          dispatch(cancelInterviewRoundAction(payload));
          setTimeout(() => {
            dispatch(getInterviewSheduleList(tableStates));
            setCancelConfirmBoxVisibility(false);
          }, 250);
        }}
      />
      <InterviewDetails data={interviewDetails} onHide={onTogglePopup} show={showPopup} />
      <DataTable
        name={TABLE_NAME.SCHEDULED_INTERVIEWS}
        columns={SCHEDULED_INTERVIEW_COLUMNS}
        data={scheduleInterviewList}
        exportData={scheduleInterviewExport}
        searchPlaceHolder="Search by Candidate name"
        totalRecords={interviewSchedule?.pagination?.totalRecords}
        onPagination={handlePagination}
        onSearch={debounce(handleSearch, DEBOUNCE_TIMEOUT)}
        onSort={handleSort}
      />
    </>
  );
};

export default InterviewSchedule;
